import { motion } from "framer-motion";
import {
  ShieldCheck,
  Lock,
  Server,
  FileCheck,
  MessageCircleQuestion,
  Mail,
} from "lucide-react";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@repo/ui";

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, ease: "easeOut" },
  },
};

const TRUST_BADGES = [
  { icon: ShieldCheck, label: "HIPAA Compliant" },
  { icon: Lock, label: "256-bit Encryption" },
  { icon: Server, label: "99.9% Uptime SLA" },
  { icon: FileCheck, label: "DEA Audit-Ready" },
];

const FAQ_ITEMS = [
  {
    question: "Can I cancel anytime?",
    answer:
      "Yes. There are no long-term contracts. You can cancel your subscription at any time from your account settings. Your access continues until the end of your current billing period.",
  },
  {
    question: "Is my data HIPAA compliant?",
    answer:
      "Absolutely. C2 Vault is built from the ground up with HIPAA compliance in mind. All data is encrypted at rest and in transit, access is role-based, and we maintain a full audit trail of every action.",
  },
  {
    question: "Can I switch between monthly and annual billing?",
    answer:
      "Yes. To switch your billing cycle, contact our support team at support@c2vault.com and we'll handle the change for you. If you switch from monthly to annual, you'll receive a prorated credit for the remainder of your current month.",
  },
];

export function SubscribeExtras() {
  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={{
        hidden: {},
        visible: { transition: { staggerChildren: 0.12, delayChildren: 0.3 } },
      }}
      className="w-full max-w-2xl mx-auto mt-12 space-y-12"
    >
      {/* Trust badges */}
      <motion.div
        variants={fadeUp}
        className="grid grid-cols-2 sm:grid-cols-4 gap-4"
      >
        {TRUST_BADGES.map(({ icon: Icon, label }) => (
          <div
            key={label}
            className="flex flex-col items-center gap-2 rounded-xl border border-border bg-surface px-4 py-4 text-center"
          >
            <Icon className="h-5 w-5 text-primary" />
            <span className="text-xs font-medium text-text-secondary">
              {label}
            </span>
          </div>
        ))}
      </motion.div>

      {/* FAQ */}
      <motion.div variants={fadeUp}>
        <div className="flex items-center gap-2 mb-4">
          <MessageCircleQuestion className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold text-foreground">
            Frequently Asked Questions
          </h2>
        </div>
        <div className="rounded-xl border border-border bg-surface px-6">
          <Accordion type="single" collapsible>
            {FAQ_ITEMS.map((item, index) => (
              <AccordionItem
                key={index}
                value={`faq-${index}`}
                className={
                  index === FAQ_ITEMS.length - 1 ? "border-b-0" : undefined
                }
              >
                <AccordionTrigger className="text-sm text-foreground hover:no-underline">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-text-secondary leading-relaxed">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </motion.div>

      {/* Need help CTA */}
      <motion.div variants={fadeUp} className="text-center pb-8">
        <p className="text-sm text-muted-foreground">
          Have questions?{" "}
          <a
            href="mailto:support@c2vault.com"
            className="inline-flex items-center gap-1 font-medium text-primary hover:underline underline-offset-2"
          >
            <Mail className="h-3.5 w-3.5" />
            support@c2vault.com
          </a>
        </p>
      </motion.div>
    </motion.div>
  );
}
